// Implements: F-UIX-019, F-IDN-027, F-UIX-010
/**
 * Plataforma > Organizaciones (mockup v3 P3, `vOrgs`): every organization of the platform with
 * Todas / Activas / Suspendidas, create, rename, invite an active organization's admin (its first
 * one arrives this way: the platform admin holds no seat in an organization they created) and
 * suspend or reactivate (C1).
 *
 * The month's spend comes from the platform totals the page reads and formats (money is formatted
 * only by the admin and backoffice modules, OD-12), handed in as text; without it the column says "—". Each write is sent once; a refusal is
 * shown with the server's sentence and the list keeps the server's state. The status sent is
 * always the OTHER one: an active organization is suspended, a suspended one reactivated.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { Tabs, tabPanelProps } from "../../../shared/ui/molecules/Tabs";
import type { OrganizationStatus, OrganizationView } from "../../../types/organization";
import { NameDialog } from "../components/NameDialog";
import { OrgStatusDialog } from "../components/OrgStatusDialog";
import styles from "../components/Organization.module.css";
import { OrganizationsTable } from "../components/OrganizationsTable";
import {
  useCreateOrganization,
  useOrganizationList,
  useRenameOrganization,
  useSetOrganizationStatus,
} from "../hooks/useOrganizations";
import { InviteAdminContainer } from "./InviteAdminContainer";
import { writeProblem } from "./PeopleContainer";

type Filter = "all" | OrganizationStatus;

const FILTERS: readonly Filter[] = ["all", "active", "suspended"];

export function otherStatus(status: OrganizationStatus): OrganizationStatus {
  return status === "active" ? "suspended" : "active";
}

export interface PlatformOrganizationsContainerProps {
  /** Each organization's spend this month, as text, or `null` when the totals are not available. */
  readonly spend: ReadonlyMap<string, string> | null;
}

export function PlatformOrganizationsContainer({ spend }: PlatformOrganizationsContainerProps): ReactElement {
  const { t } = useTranslation();
  const list = useOrganizationList();
  const create = useCreateOrganization();
  const rename = useRenameOrganization();
  const setStatus = useSetOrganizationStatus();
  const [filter, setFilter] = useState<Filter>("all");
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<OrganizationView | null>(null);
  const [changing, setChanging] = useState<OrganizationView | null>(null);
  const [invitingAdmin, setInvitingAdmin] = useState<OrganizationView | null>(null);
  const [dialogProblem, setDialogProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const all = list.data?.organizations ?? [];
  const count = (wanted: Filter): number =>
    wanted === "all" ? all.length : all.filter((organization) => organization.status === wanted).length;
  const shown = filter === "all" ? all : all.filter((organization) => organization.status === filter);
  const labels: Readonly<Record<Filter, string>> = {
    all: t("organization.platform.tab.all"),
    active: t("organization.platform.tab.active"),
    suspended: t("organization.platform.tab.suspended"),
  };

  const failed = (error: unknown): string => writeProblem(asApiError(error), t("common.state.error"));

  function spendOf(organizationId: string): string | null {
    return spend?.get(organizationId) ?? null;
  }

  function confirmStatus(): void {
    if (changing === null || setStatus.isPending) return;
    const target = changing;
    setProblem(null);
    setStatus.mutate(
      { id: target.id, status: otherStatus(target.status) },
      {
        onSettled: () => {
          setChanging(null);
        },
        onError: (error) => {
          setProblem(failed(error));
        },
      },
    );
  }

  function body(): ReactElement {
    if (list.isError) {
      return (
        <ErrorState
          headingLevel={3}
          error={asApiError(list.error)}
          onRetry={() => {
            void list.refetch();
          }}
        />
      );
    }
    if (list.data === undefined) return <LoadingState label={t("organization.platform.loading")} />;
    if (all.length === 0) {
      return (
        <EmptyState
          headingLevel={3}
          icon="building"
          title={t("organization.platform.empty.title")}
          body={t("organization.platform.empty.body")}
        />
      );
    }
    if (shown.length === 0) {
      return (
        <EmptyState
          headingLevel={3}
          icon="building"
          title={t("organization.platform.emptyFilter.title")}
          body={t("organization.platform.emptyFilter.body")}
        />
      );
    }
    return (
      <OrganizationsTable
        organizations={shown}
        spendOf={spendOf}
        onRename={(organization) => {
          setDialogProblem(null);
          setRenaming(organization);
        }}
        onInviteAdmin={setInvitingAdmin}
        onStatus={(organization) => {
          setChanging(organization);
        }}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Tabs
          label={t("organization.platform.tabs")}
          idBase="organizations"
          items={FILTERS.map((id) => ({ id, label: labels[id], count: count(id) }))}
          selected={filter}
          onSelect={(id) => {
            const next = FILTERS.find((candidate) => candidate === id);
            if (next !== undefined) setFilter(next);
          }}
        />
        <span className={styles.spacer} />
        <Button
          variant="primary"
          hasPopup="dialog"
          onClick={() => {
            setDialogProblem(null);
            setCreating(true);
          }}
        >
          <Icon name="plus" size="sm" />
          {t("organization.platform.create")}
        </Button>
      </div>
      {problem === null ? null : (
        <p className={styles.problem} role="alert">
          {problem}
        </p>
      )}
      <div {...tabPanelProps("organizations", filter)}>{body()}</div>
      {creating ? (
        <NameDialog
          title={t("organization.platform.createTitle")}
          label={t("organization.platform.nameLabel")}
          submitLabel={t("organization.platform.createSubmit")}
          pending={create.isPending}
          problem={dialogProblem}
          onSubmit={(name) => {
            create.mutate(name, {
              onSuccess: () => {
                setCreating(false);
              },
              onError: (error) => {
                setDialogProblem(failed(error));
              },
            });
          }}
          onCancel={() => {
            setCreating(false);
          }}
        />
      ) : null}
      {renaming === null ? null : (
        <NameDialog
          key={renaming.id}
          title={t("organization.platform.renameTitle")}
          label={t("organization.platform.nameLabel")}
          submitLabel={t("organization.teams.save")}
          initial={renaming.name}
          pending={rename.isPending}
          problem={dialogProblem}
          onSubmit={(name) => {
            rename.mutate(
              { id: renaming.id, name },
              {
                onSuccess: () => {
                  setRenaming(null);
                },
                onError: (error) => {
                  setDialogProblem(failed(error));
                },
              },
            );
          }}
          onCancel={() => {
            setRenaming(null);
          }}
        />
      )}
      {invitingAdmin === null ? null : (
        <InviteAdminContainer
          key={invitingAdmin.id}
          organization={invitingAdmin}
          onClose={() => {
            setInvitingAdmin(null);
          }}
        />
      )}
      {changing === null ? null : (
        <OrgStatusDialog
          key={changing.id}
          organization={changing}
          pending={setStatus.isPending}
          onConfirm={confirmStatus}
          onCancel={() => {
            setChanging(null);
          }}
        />
      )}
    </div>
  );
}
